import { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { parseISO } from 'date-fns';
import { Loading } from 'components/UI/Loading/Loading';
import { Button } from 'components/UI/Button/Button';
import { EventFiltersContext } from 'components/Shared/EventFiltersProvider';
import { Modal as MuiModal } from '@mui/material';
import { selectPerformance } from 'services/BookingModalService';
import { BookingModalHeader } from './BookingModalHeader/BookingModalHeader';
import { BookingModalCallout } from './BookingModalCallout/BookingModalCallout';
import { BookingModalPromo } from './BookingModalPromo/BookingModalPromo';
import { BookingModalSelectTime } from './BookingModalSelectTime/BookingModalSelectTime';
import { isUserLoggedIn } from './booking-modal-utils';

import './BookingModal.scss';

export const BookingModal = (props) => {
  const {
    modalIsOpen,
    closeModalHandler,
    prodSeasonId,
    performanceId,
    performanceDate,
    sitecoreItemId,
    promoCodeFromUrl,
  } = props;
  const { startDate } = useContext(EventFiltersContext);
  const [eventData, setEventData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [performanceIdSelected, setPerformanceIdSelected] = useState(null);
  const [dateSelected, setDateSelected] = useState(null);
  const [continueButtonIsDisabled, setContinueButtonIsDisabled] = useState(true);
  const eventHasPerformances = eventData?.PerformanceDateTimes?.length > 0;
  const isSingleDatePerformance = eventData?.PerformanceDateTimes?.length === 1;

  /*
   * Remove timezone data from CalendarData DateValues so that the Datepicker
   * is always consistent with the default day selected and the performance shown
   */
  const removeTimezonesFromResponse = (response) => {
    const timezoneRegex = /^(.*)T.*$/g;

    response.CalendarData.Years.forEach((years) =>
      years.Months.forEach((month) =>
        month.Days.forEach((day) => {
          // Change '2022-08-26T00:00:00+10:00' to '2022-08-26' for Day values
          const dateValueWithNoTimezone = day.DateValue.replace(timezoneRegex, '$1');
          day.DateValue = dateValueWithNoTimezone;
        })
      )
    );
  };

  const getEventData = async () => {
    const performanceUrlParams = [
      prodSeasonId && `prodSeasonId=${prodSeasonId}`,
      performanceId && `performanceId=${performanceId}`,
      performanceDate && `performanceDate=${performanceDate}`,
      promoCodeFromUrl && `promoCode=${promoCodeFromUrl}`,
      sitecoreItemId && `sitecoreItemId=${sitecoreItemId}`,
    ]
      .filter(Boolean)
      .join('&');

    try {
      const response = await selectPerformance(performanceUrlParams);
      if (response) {
        removeTimezonesFromResponse(response);
        setEventData(response);
        setIsLoading(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  /*
   * Fetch the event data for this event
   */
  useEffect(() => {
    getEventData();
  }, []);

  /*
   * When the event data is loaded, select the default date from the
   * payload OR set it as the date with the first available perfomance
   */
  useEffect(() => {
    if (eventData) {
      const defaultSelectedCalendarDate = startDate || eventData?.CalendarData.SelectedDate;
      const findFirstAvailablePerformanceDate = () =>
        eventData?.PerformanceDateTimes?.[0]?.PerformanceDateTime;
      const findFirstPerformanceDate = eventData?.PerformanceDateTimes[0].PerformanceDateTime;

      const defaultSelectedDate = parseISO(
        defaultSelectedCalendarDate ||
          findFirstAvailablePerformanceDate() ||
          findFirstPerformanceDate
      );

      setDateSelected(defaultSelectedDate);
    }
  }, [eventData]);

  /*
   * When a specific performanceId (i.e. performance time) is selected
   * enable the continue button to allow the user to progress
   */
  useEffect(() => {
    if (performanceIdSelected) {
      setContinueButtonIsDisabled(false);
    }
  }, [performanceIdSelected]);

  const performanceIdSelectedHandler = (timeId) => {
    setPerformanceIdSelected(timeId);
  };

  const calendarDateClickHandler = (selectedDate) => {
    setDateSelected(parseISO(selectedDate));
    setPerformanceIdSelected(null);
    setContinueButtonIsDisabled(true);
  };

  const continueToSeatsButtonHandler = () => {
    window.trackEvent('select_item_date_time', {
      performanceIdSelected,
      eventData,
    });
    if (performanceIdSelected) {
      window.location.href = `${window.location.origin}/cart/select-seats?perf_no=${performanceIdSelected}&prodseasonid=${prodSeasonId}`;
    }
  };

  const noDatesAvailableContent = (
    <div className="no-results__container">
      <h2 className="h4 cart-content-heading">No Available Performances</h2>
      <p>Sorry, there are no available performances to choose from.</p>
    </div>
  );

  const bookingModalContent = (
    <>
      <BookingModalHeader
        closeModalHandler={closeModalHandler}
        heading={eventData?.CurrentEventName}
      />
      {!isUserLoggedIn(eventData?.MembershipAccountUrl) && <BookingModalCallout />}
      <BookingModalPromo
        prodSeasonId={eventData?.ProdSeasonId}
        performanceId={eventData?.PerformanceId}
        promocodeApplied={eventData?.PromoCodeApplied}
        promocodeName={eventData?.PromoCodeName}
        setEventData={setEventData}
        membershipAccountUrl={eventData?.MembershipAccountUrl}
        preferencesAccountUrl={eventData?.PreferencesAccountUrl}
        promoCodeFromUrl={promoCodeFromUrl}
      />
      {eventHasPerformances && !isLoading ? (
        <div className={`${isSingleDatePerformance ? 'is-single-perf' : ''}`}>
          <BookingModalSelectTime
            {...eventData}
            dateSelected={dateSelected}
            performanceIdSelected={performanceIdSelected}
            performanceIdSelectedHandler={performanceIdSelectedHandler}
            calendarDateClickHandler={calendarDateClickHandler}
          />
          <div className="booking-modal__continue-button">
            <Button
              extendClass="continue-button"
              onClick={continueToSeatsButtonHandler}
              disabled={continueButtonIsDisabled}
            >
              Continue to seat selection
            </Button>
          </div>
        </div>
      ) : (
        noDatesAvailableContent
      )}
    </>
  );

  const classes = ['booking-modal-content', isLoading ? 'modal-is-loading' : ''].join(' ');

  return (
    <MuiModal
      open={modalIsOpen}
      onClose={closeModalHandler}
      aria-labelledby="booking-modal"
      sx={{
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <div className="booking-modal" data-testid="booking-modal">
        <div className={classes}>{isLoading ? <Loading /> : bookingModalContent}</div>
      </div>
    </MuiModal>
  );
};

BookingModal.propTypes = {
  modalIsOpen: PropTypes.bool,
  closeModalHandler: PropTypes.func,
  prodSeasonId: PropTypes.number,
  performanceId: PropTypes.number,
  performanceDate: PropTypes.string,
  sitecoreItemId: PropTypes.number,
  promoCodeFromUrl: PropTypes.string,
};
